/**
 * Created by mrocha on 08/19/16.
 */
import factoryService from 'FactoryService';
import listService from 'ListService';
import util from "util/Util";
import constants from 'Constants';
import focusController from 'FocusController.js';
import sequenceService from 'SequenceService.js';
import mobileGroupService from "./mobile/MobileGroupService";
import notificationService from './NotificationService';
import utilMobile from '../mobile/commons/MobileGeneralFunctions';
import geometryService from './GeometryService';

const SILENT = constants.MODEL_SILENT;
const GROUP_MARGIN = 10;
const GROUP_FACTORY_KEY = 'control_group';

class GroupService {

    createNewGroup(factoryKey, dropPos) {

        const factoryParams = factoryService.factories[factoryKey];
        const groupControl = factoryParams.factory.createView(factoryKey, dropPos);

        // if we dropped a pre-configured Group with Children
        let childList = groupControl.model.get('childList');
        if (childList && childList.length) {
            childList.forEach((child) => {
                // create View from the model
                const _fp = factoryService.factories[child.get('factoryKey')];
                const vw = _fp.factory.createView(child.get('factoryKey'), child.get('pos'));
                child._events = vw.model._events;
                vw.groupId = groupControl.id;
                vw.model = child;
                vw.id = child.get('id');
                listService.addObject(vw.id, vw);
            });
        }

        return groupControl;
    }

    groupControls(groupIn, selectedControls) {
        let groupTop = Number.MAX_VALUE;
        let groupLeft = Number.MAX_VALUE;
        let groupBottom = Number.MIN_VALUE;
        let groupRight = Number.MIN_VALUE;
        let outerGroupview;
        let group;

        let selectedControlsArray = selectedControls;
        if (selectedControlsArray.size >= 1 && selectedControlsArray.first().groupId) {
            outerGroupview = listService.getObject(selectedControlsArray.first()._groupId);
        }

        // Find rectangle that encompasses selection
        selectedControlsArray.map(view => {
            return {
                width: util.extractNum(view.model.get('width')),
                height: util.extractNum(view.model.get('height')),
                top: util.extractNum(view.model.get('top')),
                left: util.extractNum(view.model.get('left'))
            };
        }).forEach(item => {
            groupTop = Math.min(groupTop, item.top);
            groupLeft = Math.min(groupLeft, item.left);
            groupBottom = Math.max(groupBottom, item.top + item.height);
            groupRight = Math.max(groupRight, item.left + item.width);
        });

        // Expand group to create a margin
        const
            groupWidth = Math.round(groupRight - groupLeft + (GROUP_MARGIN * 2)),
            groupHeight = Math.round(groupBottom - groupTop + (GROUP_MARGIN * 2));

        const dropPos = {
            left: groupLeft - GROUP_MARGIN <= 0 ? 0 : groupLeft - GROUP_MARGIN,
            top: groupTop - GROUP_MARGIN <= 0 ? 0 : groupTop - GROUP_MARGIN
        };

        // The factory knows to create properties for the model
        const
            factoryParams = factoryService.factories[GROUP_FACTORY_KEY];//,
        //group;// = factoryParams.factory.createView(GROUP_FACTORY_KEY, dropPos);

        // in case of new group.
        if (!groupIn) {
            group = factoryParams.factory.createView(GROUP_FACTORY_KEY, dropPos);
            sequenceService.addControlNumber();
            if (!group._groupId && outerGroupview && outerGroupview.id) { //At this point it is necessary to know if the group is in another group, related to PF-4068
                group._groupId = outerGroupview.id;
            }

            listService.addObject(group.id, group);
        }// in case of Redo action.
        else {
            group = groupIn;
            listService.addObject(group.id, group, { pageId: group.pageId });
        }

        let items = [];
        if (selectedControlsArray.size >= 1) {
            let groupHasMobileObjects = false;
            selectedControlsArray.forEach(view => {
                let viewTop = util.extractNum(view.model.get('top')) - groupTop + GROUP_MARGIN;
                let viewLeft = util.extractNum(view.model.get('left')) - groupLeft + GROUP_MARGIN;
                view.model.set('top', viewTop, SILENT);
                view.model.set('left', viewLeft, SILENT);
                view.options.pos.top = viewTop;
                view.options.pos.left = viewLeft;
                let initDevice = view.model.get('deviceDesktop');
                if (initDevice) {
                    view.model.initDevice.top = viewTop;
                    view.model.initDevice.left = viewLeft;
                    view.model.attributes.deviceDesktop.top = viewTop;
                    view.model.attributes.deviceDesktop.left = viewLeft;
                }

                let isMobile = mobileGroupService.regroupControl(view.model.get('id'), group.id);
                if (!groupHasMobileObjects) {
                    groupHasMobileObjects = isMobile;
                }

                view._groupId = group.id; // PF-1105
                view.model.resetLastState();
                items.push(view.model);
            });

            if (groupHasMobileObjects) {
                utilMobile.addedNewObjectToDesktopCanvas(group);
                notificationService.info('Multiple objects have been modified. This change will affect Desktop and Mobile app design.');
            }
        }

        if (outerGroupview) {
            group._groupId = outerGroupview.id;
            let itemsGroup = outerGroupview.model.get('childList');
            let attachItemsGroup = [];

            const outerItems = itemsGroup.filter(item => !selectedControlsArray.map(view => view.model.id).includes(item.id));
            if (outerItems && outerItems.length) {
                attachItemsGroup.push(...outerItems);
            }

            attachItemsGroup.push(group.model);
            outerGroupview.model.set('childList', attachItemsGroup, SILENT);
            outerGroupview.model.resetLastState();
            outerGroupview.render();
        }

        group.model.set('height', `${groupHeight}px`, SILENT);
        group.model.set('width', `${groupWidth}px`, SILENT);
        group.model.set('childList', items, SILENT);
        group.model.resetLastState();
        group.render();

        focusController.clearAllSelected();

        return group;
    }

    //=================================================================================================
    // Removes a group control after moving its contained controls to the next parent in the dom.
    //
    // That parent is either another group or the page. If the group view has a groupId reference
    // then the new parent is the group having that ID. Otherwise the new parent is the pageId
    // reference.
    //
    // The groupIds of the grouped controls are updated to the value of the parent group if any.
    // Otherwise those groupId references are deleted.
    //=================================================================================================
    ungroupControls(groupControl) {

        // 1. Determine this group's parent: either another group or the page
        const parentId = groupControl.groupId ? groupControl.groupId : groupControl.pageId;

        // 2. Get group's child controls, omitting form itself
        const kids = listService.objectList
            .filter((v, k) => v.groupId === groupControl.id && v.id !== constants.FORM_RAW_ID)

        // 3. De-select all
        focusController.clearAllSelected();

        kids.forEach((v, id) => {

            // 4. Update parent references of child controls
            if (groupControl.groupId) {
                v.groupId = parentId;
            } else {
                v.groupId = undefined;
                v.pageId = parentId;

                mobileGroupService.ungroupControl(v.model.get('id'), undefined, parentId);
            }

            // 5. IN DOM: move child controls to parent
            $(constants.ID_SEL + id).appendTo(constants.FORM_ID);
            // Relocate origin relative to page
            let viewLeft = Number(v.model.get('left')) + Number(groupControl.model.get('left'));
            let viewTop = Number(v.model.get('top')) + Number(groupControl.model.get('top'));
            v.model.set('left', viewLeft, { silent: true });
            v.model.set('top', viewTop, { silent: true });
            if (!v.options.pos) {
                v.options.pos = {};
            }
            v.options.pos.top = viewTop;
            v.options.pos.left = viewLeft;
            let initDevice = v.model.get('deviceDesktop');
            if (initDevice) {
                v.model.initDevice.top = viewTop;
                v.model.initDevice.left = viewLeft;
                v.model.attributes.deviceDesktop.top = viewTop;
                v.model.attributes.deviceDesktop.left = viewLeft;
            }

            v.model.resetLastState();

            // 6. Multi-select the child controls
            v.render();
            listService.addToSelected(id);
            focusController.selectionRectangle(id, true, false);
        });

        // 8. Check ungrouping for mobile
        this.ungroupMobile(groupControl, kids);

        // 9. Remove group control from system (invisible)  AND from DOM (rendered)
        listService.destroyObject(groupControl.id);
        $(constants.ID_SEL + groupControl.id).remove();

        return kids;
    }

    addControlToGroup(targetGroup, control) {
        const grpVw = listService.getObject(targetGroup);
        control.groupId = grpVw.id;
        sequenceService.resetTabIndex(control.model.attributes.tabIndex);
        sequenceService.tabIndexParentId = grpVw.id;
        control.model.attributes.tabIndex = sequenceService.nextTabIndex();
        control.model.attributes.parentBorder = grpVw.model.get("borderSize");
    
        grpVw.model.get('childList').push(control.model);
        $(constants.ID_SEL + control.id).appendTo(`${constants.ID_SEL}${'groupItems'+ targetGroup}`);
    }

    ungroupMobile(groupControl, kids) {

        const kidsAreSameType = (data, factoryKey) => {
            return data.toArray().every(kid => kid.model.get('factoryKey') === factoryKey);
        };

        const sameType = (kidsAreSameType(kids, "control_radio_button") || kidsAreSameType(kids, "control_checkbox"));

        if (util.isGroupContainer(groupControl.model.get('factoryKey')) && listService.controlHasSection(groupControl.model.get('id')) && sameType) {
            notificationService.warning(`${groupControl.model.get('name')} has been deleted. This change will affect Desktop and Mobile app design.`);
        }

        if (listService._mobileSections) {
            eventBus.trigger(constants.EV_VALIDATE_PULSE_ICON_EVENT);
        }
    }

    getPositionNestedGroups(property, groupParent) {
        let value = util.extractNum(groupParent.model.get(property));
        if (groupParent.groupId) {
            value = value + this.getPositionNestedGroups(property, listService.getObject(groupParent.groupId));
        }
        return value;
    }

    getGroupDimensions(grpVw){
         return { width: util.extractNum(grpVw.model.get('width')),
                 height: util.extractNum(grpVw.model.get('height')),
                 left: this.getPositionNestedGroups('left', grpVw),
                 top: this.getPositionNestedGroups('top', grpVw),
                 border: util.extractNum(grpVw.model.get('borderSize'))}                 
    }

    droppedOnGroupObject(ev, control, dropPos) {

        const targetControl = listService.getObject(ev.currentTarget.id);
        let targetGroup;

        if (targetControl) {
            const isGroup = this.isGroup(targetControl.model.get('factoryKey'));
            targetGroup = isGroup ? targetControl.model.get("id") : targetControl._groupId;

            if(targetGroup){
                this.addControlToGroup(targetGroup,control,dropPos);
            }
        }

        return listService.getObject(targetGroup);
    }

    findParentGroup(ev){
        const targetControl = listService.getObject(ev.id);
        let targetContainer;
        if (targetControl) {
            const isGroup = this.isGroup(targetControl.model.get('factoryKey'));            
            targetContainer = !isGroup ? this.findParentGroup(ev.offsetParent) : targetControl;
        }
        return targetContainer;
    }

    checkGroupBorders(controlModel, groupModel){

        const group = $(`${constants.ID_SEL}${'groupItems'+ groupModel.model.get("id")}`);
        const control = $(`${constants.ID_SEL}${controlModel.model.get("id")}`);

        const groupWidth = util.extractNum(group.width());
        const controlTruePosition = util.extractNum(controlModel.model.get("left")) + util.extractNum(controlModel.model.get("width"));

        let diff = 0;

        if(controlTruePosition > groupWidth){
            diff = controlTruePosition - groupWidth;
            let currentLeft = util.extractNum(control.css("left"));
            control.css({"left": currentLeft - diff + "px"});
        }
    }

    checkGroupBounds(group, cdata, dropPos){

        const dimensions = this.getGroupDimensions(group);
        const maxLeft = dimensions.width - cdata.cx;
        const maxTop = dimensions.height - cdata.cy;
        let newPos = {left: 0, top: 0};

         //Checks how much the control is out of bound of the group and calculates to compensate that difference
        let findNewPos = (pos,c,max) => {
            let diff = 0;
            if(pos + c > max){
                diff = (pos + c) - max;
            }
            return pos - diff;
        }

        newPos.left = findNewPos(dropPos.left, cdata.cx, maxLeft);
        newPos.top = findNewPos(dropPos.top, cdata.cy, maxTop);
    
        return newPos;
    }

    isGroup(factoryKey) {
        return constants.GROUPS_CONTROLS_KEYS.includes(factoryKey);
    }
}

const groupService = new GroupService();
export default groupService;